import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { table } from '../../lib/manifest';
import '../raidplanner.css';

/**
 * Raid Planner (port of the app's RaidPlannerPage): a raid encounter on pryanie#2536's map artwork (Destiny Bulletin,
 * used with permission, drawn unaltered), the 6 Guardians gliding to their spots step by step, enemies, objectives,
 * arrows and who does what. Plans are the app's own plan files (format 1), final boss first.
 */

type XY = [number, number];
interface Role { id: string; name: string; color: string }
interface Obj { type: string; x: number; y: number; label?: string; shield?: boolean; lit?: boolean; prefix?: string }
interface Step { title: string; text?: string; positions?: Record<string, XY>; tasks?: Record<string, string>; enlightened?: string[]; holding?: Record<string, string>; objects?: Obj[]; actions?: { role: string; to: XY; kind: string }[] }
interface Plan {
  id: string; raid: string; encounter: string; order: number; kind?: string; status?: string; credits?: string; theme?: Record<string, string>;
  checkpoint?: { url: string; activity: string; checkpoint: string; fallback: string } | null;
  map: {
    width: number; height: number; views?: Record<string, number[]>; markerScale?: Record<string, number>;
    background?: { image: string; x?: number; y?: number; width?: number; height?: number; opacity?: number; credit?: string };
    artPanel?: { rect: number[] };
  };
  roles: Role[]; steps: Step[]; loot?: { name: string; kind: string; rect: number[] }[]; ambient?: any[];
  bossIcon?: { image?: string; width?: number; rect?: number[] }; symbol?: number[];
}

const BASE = '/maps/raids/';
const NS = 'http://www.w3.org/2000/svg';
const store = {
  get: (k: string, d = '') => { try { return localStorage.getItem('d2c.raid.' + k) ?? d; } catch { return d; } },
  set: (k: string, v: string) => { try { localStorage.setItem('d2c.raid.' + k, v); } catch { /* private window */ } },
};
let plansP: Promise<Plan[]> | undefined;
const loadPlans = () => (plansP ??= fetch(BASE + 'plans.json').then((r) => r.json()).then((l: Plan[]) => {
  for (const p of l) {
    if (!p.steps?.length) p.steps = [{ title: p.encounter }];   // a map to read: one plain step
    p.roles ??= [];
  }
  return l.sort((a, b) => b.order - a.order);
}));
const mapOnly = (p: Plan) => p.steps.every((s) => !Object.keys(s.positions ?? {}).length && !Object.keys(s.tasks ?? {}).length);
const TH_DEFAULT: Record<string, string> = {
  page: '#060D08', glass: 'rgba(14,30,18,.72)', line: 'rgba(160,230,170,.20)', text: '#E4EFE4', muted: '#93A897', accent: '#8EF29B', accent2: '#C9F7A6', board: '#071009', boardGlow: 'rgba(80,170,90,.22)',
};
const themeVars = (t: Record<string, string> = {}) => Object.fromEntries(Object.keys(TH_DEFAULT).map((k) => [`--rp-${k}`, t[k] || TH_DEFAULT[k]])) as CSSProperties;

/** A piece of the artwork (2560-wide artwork pixels) as an SVG picture: the file shown through a window, nothing cut on disk. */
function ArtCrop({ plan, rect, x, y, w, h, clip }: { plan: Plan; rect: number[]; x: number; y: number; w: number; h: number; clip?: string }) {
  const bg = plan.map.background!;
  const ah = 2560 * ((bg.height ?? plan.map.height) / (bg.width ?? plan.map.width));
  return (
    <svg x={x} y={y} width={w} height={h} viewBox={rect.join(' ')} preserveAspectRatio="none" clipPath={clip}>
      <image href={BASE + bg.image} width={2560} height={ah} preserveAspectRatio="none" />
    </svg>
  );
}

export default function RaidPlanner({ onClose }: { onClose: () => void }) {
  const [plans, setPlans] = useState<Plan[]>();
  const [raid, setRaid] = useState<string | null>(null);
  useEffect(() => { void loadPlans().then(setPlans); }, []);
  if (!plans) return <div className="rp-frame rp-wait">Loading the raid plans...</div>;
  if (!raid) return <RaidTiles plans={plans} open={setRaid} onClose={onClose} />;
  return <Planner plans={plans.filter((p) => p.raid === raid)} back={() => setRaid(null)} />;
}

// ---------------- raid tiles ----------------

function RaidTiles({ plans, open, onClose }: { plans: Plan[]; open: (r: string) => void; onClose: () => void }) {
  const raids = [...new Set(plans.map((p) => p.raid))];
  return (
    <div className="rp-frame rp-tiles">
      <div className="rp-tilehead">
        <span>Pick a raid or dungeon to open its maps.</span>
        <button type="button" className="g-fullpill on" onClick={onClose}>‹  Back to the map</button>
      </div>
      <div className="rp-tilegrid">
        {raids.map((r) => {
          const list = plans.filter((p) => p.raid === r);
          const boss = list[0];
          const t = { ...TH_DEFAULT, ...boss.theme };
          const sub = boss.kind === 'boss' ? `${list.length} encounters  ·  final boss: ${boss.encounter}` : list.every(mapOnly) ? `${list.length} encounters  ·  maps to read` : `${list.length} encounters  ·  up to ${boss.encounter}`;
          return (
            <button type="button" key={r} className="rp-tile" onClick={() => open(r)} aria-label={`${r}: open the raid planner`}
              style={{ '--a': t.accent, background: `linear-gradient(135deg, color-mix(in srgb, ${t.accent} 22%, ${t.page}), ${t.page} 70%)` } as CSSProperties}>
              <span className="badge">
                {boss.map.background && <svg viewBox="0 0 104 104" width={104} height={104}><ArtCrop plan={boss} rect={boss.symbol ?? [41, 64, 120, 120]} x={0} y={0} w={104} h={104} /></svg>}
              </span>
              <span className="words"><b>{r.toUpperCase()}</b><span>{sub}</span></span>
            </button>
          );
        })}
      </div>
      <p className="rp-credit">Map art by pryanie#2536 (Destiny Bulletin, thegamepost.com), used with permission.</p>
    </div>
  );
}

// ---------------- the planner ----------------

const GLIDE_MS = 1100;
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function Planner({ plans, back }: { plans: Plan[]; back: () => void }) {
  const nav = useNavigate();
  const [planId, setPlanId] = useState(() => { const k = store.get('plan'); return plans.some((p) => p.id === k) ? k : plans[0].id; });
  const plan = plans.find((p) => p.id === planId) ?? plans[0];
  const [step, setStep] = useState(0);
  const [focus, setFocus] = useState<string | null>(null);
  const [stepsOn, setStepsOn] = useState(store.get('steps') === '1');
  const [detail, setDetail] = useState(() => Number(store.get('detail', '100')));
  const [arrived, setArrived] = useState(-1);
  const [names, setNames] = useState<Record<string, string>>({});
  const [namesOpen, setNamesOpen] = useState(false);
  const [trails, setTrails] = useState<{ id: string; from: XY; to: XY }[]>([]);
  const [cpText, setCpText] = useState('Copy final checkpoint');
  const [box, setBox] = useState({ w: 0, h: 0 });
  const frame = useRef<HTMLDivElement>(null);
  const playerEls = useRef<Record<string, SVGGElement | null>>({});
  const cur = useRef<Record<string, XY>>({});
  const glideToken = useRef(0);
  const lastPlan = useRef('');
  const ambRef = useRef<SVGGElement>(null);

  const bg = plan.map.background;
  const W = plan.map.width, H = plan.map.height;
  const views = plan.map.views ?? { full: [0, 0, W, H] };
  const viewKey = views.full ? 'full' : Object.keys(views)[0];
  const view = views[viewKey];
  const MS = plan.map.markerScale?.[viewKey] ?? 1;
  const k = bg ? (bg.width ?? W) / 2560 : 1;
  const AX = (v: number) => (bg?.x ?? 0) + v * k, AY = (v: number) => (bg?.y ?? 0) + v * k;
  const artRect = bg && plan.map.artPanel ? [AX(plan.map.artPanel.rect[0]), AY(plan.map.artPanel.rect[1]), plan.map.artPanel.rect[2] * k, plan.map.artPanel.rect[3] * k] : null;
  const only = mapOnly(plan);
  const docked = !artRect && !only;
  const s = plan.steps[step] ?? plan.steps[0];
  const roleById = useMemo(() => Object.fromEntries(plan.roles.map((r) => [r.id, r])), [plan]);
  const crota = plan.raid === "Crota's End";

  // a new encounter: its remembered job and names, step 1
  useEffect(() => {
    store.set('plan', plan.id);
    const f = store.get('role.' + plan.id);
    setFocus(f && plan.roles.some((r) => r.id === f) ? f : null);
    setNames(Object.fromEntries(plan.roles.map((r) => [r.id, store.get(`name.${plan.id}.${r.id}`)])));
    setStep(0);
    setTrails([]);
  }, [plan]);

  // the frame's size decides the rail's width (what the artwork leaves over at this height)
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const read = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    read();   // at once, so a phone never shows the wide layout first while the observer catches up
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const narrow = box.w > 0 && box.w < 900;
  const rail = narrow ? 0 : Math.round(Math.min(380, Math.max(240, box.w - 26 - (docked ? 372 : 0) - (box.h - 2) * view[2] / view[3])));

  // players: glide to the step's places (1.1 s, ease-in-out), then the arrows appear
  useLayoutEffect(() => {
    const pos = s.positions ?? {};
    const place = (id: string, x: number, y: number) => {
      cur.current[id] = [x, y];
      playerEls.current[id]?.setAttribute('transform', `translate(${x} ${y}) scale(${MS})`);
    };
    const animate = lastPlan.current === plan.id;
    lastPlan.current = plan.id;
    const token = ++glideToken.current;
    const targets = Object.entries(pos).filter(([id]) => roleById[id]);
    if (!animate) {
      cur.current = {};
      for (const [id, [x, y]] of targets) place(id, x, y);
      setTrails([]);
      setArrived(step);
      return;
    }
    setArrived(-1);
    const from = Object.fromEntries(targets.map(([id, to]) => [id, cur.current[id] ?? to]));
    setTrails(targets.filter(([id, to]) => from[id] && Math.hypot(from[id][0] - to[0], from[id][1] - to[1]) > 30).map(([id, to]) => ({ id, from: from[id], to })));
    const start = performance.now();
    const tick = (now: number) => {
      if (token !== glideToken.current) return;
      const t = Math.min(1, (now - start) / GLIDE_MS), e = ease(t);
      for (const [id, to] of targets) place(id, from[id][0] + (to[0] - from[id][0]) * e, from[id][1] + (to[1] - from[id][1]) * e);
      if (t < 1) requestAnimationFrame(tick); else setArrived(step);
    };
    requestAnimationFrame(tick);
  }, [plan, step]);   // eslint-disable-line react-hooks/exhaustive-deps

  // the map's own life: ships, beams and breathing lights on a loop, whatever step is showing
  useEffect(() => {
    const g = ambRef.current;
    if (!g) return;
    g.replaceChildren();
    const loops = buildAmbient(g, plan.ambient ?? []);
    if (!loops.length) return;
    let raf = 0, last = 0;
    const t0 = performance.now();
    const frameFn = (now: number) => {
      raf = requestAnimationFrame(frameFn);
      if (now - last < 16) return;
      last = now;
      const t = (now - t0) / 1000;
      for (const f of loops) f(t);
    };
    raf = requestAnimationFrame(frameFn);
    return () => cancelAnimationFrame(raf);
  }, [plan]);

  // keyboard: left / right step
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') setStep((x) => Math.min(plan.steps.length - 1, x + 1));
      else if (e.key === 'ArrowLeft') setStep((x) => Math.max(0, x - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [plan]);

  const pickFocus = (id: string | null) => { setFocus(id); store.set('role.' + plan.id, id ?? ''); };
  const showSteps = (on: boolean) => { setStepsOn(on); store.set('steps', on ? '1' : '0'); };
  const nameOf = (r: Role) => names[r.id]?.trim() || null;
  const openLoot = (name: string) => nav(`/guardian/operations/d2-info?item=${encodeURIComponent(name)}`);
  const artOpacity = bg ? (bg.opacity ?? 1) * (detail / 100) : 0;
  const weapons = bg ? (plan.loot ?? []).filter((l) => l.kind === 'weapon') : [];

  const copyCheckpoint = async () => {
    const cp = plan.checkpoint;
    if (!cp) return;
    setCpText('Copying...');
    const code = await currentCheckpoint(plan.id, cp);
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = !!code; } catch { /* no clipboard */ }
    setCpText(ok ? 'Copied: ' + code : "Couldn't copy");
    setTimeout(() => setCpText('Copy final checkpoint'), 2200);
  };

  const column = (em: number, dockedCol: boolean) => (
    <StepsColumn plan={plan} step={step} focus={focus} names={names} em={em} docked={dockedCol} crota={crota} />
  );

  return (
    <div className={`rp-frame rp-planner ${narrow ? 'narrow' : ''}`} ref={frame} style={themeVars(plan.theme)}>
      <div className="rp-map">
        <svg viewBox={view.join(' ')} preserveAspectRatio={narrow ? 'xMidYMid meet' : 'xMinYMid meet'} role="img" aria-label={`Map of ${plan.encounter}`}>
          <defs>
            <filter id="rp-soft" x="-5%" y="-5%" width="110%" height="110%"><feGaussianBlur stdDeviation={(bg?.width ?? W) / 90} /></filter>
            <clipPath id="rp-diamond" clipPathUnits="objectBoundingBox"><polygon points="0.5,0 1,0.5 0.5,1 0,0.5" /></clipPath>
            {[[-1, 0], [1, 0], [0, -1], [0, 1]].map(([dx, dy]) => {
              const sx = dx < 0 ? 1 : dx > 0 ? 0 : 0.5, sy = dy < 0 ? 1 : dy > 0 ? 0 : 0.5;
              return (
                <linearGradient key={`${dx}${dy}`} id={`rp-fade${dx}${dy}`} x1={sx} y1={sy} x2={sx + dx * 0.3} y2={sy + dy * 0.14}>
                  <stop offset="0" stopColor="var(--rp-board)" stopOpacity={0.16} />
                  <stop offset="1" stopColor="var(--rp-board)" stopOpacity={0.92} />
                </linearGradient>
              );
            })}
          </defs>
          {bg && (
            <g opacity={artOpacity}>
              {/* the artwork's own edges, mirrored and blurred, fill the bars beside it */}
              {[[-1, 0], [1, 0], [0, -1], [0, 1]].map(([dx, dy]) => {
                const bw = bg.width ?? W, bh = bg.height ?? H, x = (bg.x ?? 0) + dx * bw, y = (bg.y ?? 0) + dy * bh;
                return (
                  <g key={`${dx}${dy}`}>
                    <image href={BASE + bg.image} x={x} y={y} width={bw} height={bh} preserveAspectRatio="none" filter="url(#rp-soft)" opacity={0.9}
                      transform={`translate(${x + bw / 2} ${y + bh / 2}) scale(${dx ? -1 : 1} ${dy ? -1 : 1}) translate(${-(x + bw / 2)} ${-(y + bh / 2)})`} />
                    <rect x={x} y={y} width={bw} height={bh} fill={`url(#rp-fade${dx}${dy})`} />
                  </g>
                );
              })}
              <image href={BASE + bg.image} x={bg.x ?? 0} y={bg.y ?? 0} width={bg.width ?? W} height={bg.height ?? H} preserveAspectRatio="none" />
            </g>
          )}
          {weapons.length > 0 && (
            <g opacity={artOpacity}>
              {weapons.map((l) => {
                const g = 1.15, w = l.rect[2] * k * g, h = l.rect[3] * k * g, x = AX(l.rect[0] - l.rect[2] * (g - 1) / 2), y = AY(l.rect[1] - l.rect[3] * (g - 1) / 2);
                return (
                  <g key={l.name} className="rp-loot" style={{ transformOrigin: `${x + w / 2}px ${y + h / 2}px` }} role="button" tabIndex={0} onClick={() => openLoot(l.name)} onKeyDown={(e) => e.key === 'Enter' && openLoot(l.name)}>
                    <title>{l.name}  (open in D2 Info)</title>
                    <ArtCrop plan={plan} rect={l.rect} x={x} y={y} w={w} h={h} />
                    <rect className="ring" x={x} y={y} width={w} height={h} fill="none" stroke="#C9F7A6" strokeWidth={2.5 * k} />
                  </g>
                );
              })}
            </g>
          )}
          <g ref={ambRef} pointerEvents="none" />
          <g key={`o${plan.id}${step}`} className="rp-fade">
            {(s.objects ?? []).map((o, i) => <MapObject key={i} o={o} plan={plan} ms={MS} bossBelow={(s.objects ?? []).some((x) => x.type === 'oversoul')} />)}
          </g>
          {arrived === step && (
            <g key={`a${plan.id}${step}`} className="rp-fade">
              {(s.actions ?? []).map((a, i) => <Arrow key={i} from={(s.positions ?? {})[a.role]} a={a} color={roleById[a.role]?.color} ms={MS} dim={!!focus && focus !== a.role} />)}
            </g>
          )}
          <g key={`t${plan.id}${step}`} className="rp-fade">
            {trails.map((t) => (
              <line key={t.id} x1={t.from[0]} y1={t.from[1]} x2={t.to[0]} y2={t.to[1]} stroke={roleById[t.id]?.color} strokeWidth={2 * MS} strokeDasharray={`${3 * MS} ${7 * MS}`} opacity={focus && focus !== t.id ? 0.12 : 0.5} />
            ))}
          </g>
          <g>
            {plan.roles.map((r) => {
              const here = (s.positions ?? {})[r.id];
              const hold = (s.holding ?? {})[r.id];
              const lit = (s.enlightened ?? []).includes(r.id);
              const who = nameOf(r);
              return (
                <g key={r.id} ref={(e) => { playerEls.current[r.id] = e; }} style={{ display: here ? undefined : 'none', opacity: focus && focus !== r.id ? 0.3 : 1, transition: 'opacity .3s' }}>
                  <circle r={26} fill="none" stroke="#FFD166" strokeOpacity={0.35} strokeWidth={9} opacity={lit ? 1 : 0} />
                  <circle r={26} fill="none" stroke="#FFD166" strokeWidth={3} opacity={lit ? 1 : 0} />
                  <circle r={19} fill="#0A140C" stroke={r.color} strokeWidth={4} />
                  <text y={5} textAnchor="middle" fill={r.color} fontSize={13} fontWeight={700} fontFamily="Segoe UI, system-ui, sans-serif">{r.id}</text>
                  <g transform="translate(17,-17)">
                    {hold && hold !== 'sword' && <circle r={7} fill="#FFD166" stroke="#3A2A00" strokeWidth={2} />}
                    {hold === 'sword' && <line x1={-7} y1={7} x2={7} y2={-7} stroke="#fff" strokeWidth={4} strokeLinecap="round" />}
                  </g>
                  {who && (
                    <g transform="translate(0 38)">
                      <rect x={-(who.length * 3.6 + 8)} y={-12} width={who.length * 7.2 + 16} height={18} rx={8} fill="rgba(4,12,6,.8)" />
                      <text y={2} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={600} fontFamily="Segoe UI, system-ui, sans-serif">{who}</text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
          {artRect && (
            <>
              {stepsOn && (
                <foreignObject x={artRect[0]} y={artRect[1]} width={artRect[2]} height={artRect[3]} opacity={Math.max(artOpacity, 0.85)}>
                  {column(25 * k, false)}
                </foreignObject>
              )}
              <foreignObject x={AX(2285)} y={AY(38)} width={236 * k} height={40 * k}>
                <button type="button" className="rp-artbtn" style={{ fontSize: 15 * k, borderWidth: 1.5 * k }} onClick={() => showSteps(!stepsOn)}>
                  {stepsOn ? 'ORIGINAL GUIDE' : 'DETAILED STEPS'}
                </button>
              </foreignObject>
            </>
          )}
        </svg>
      </div>

      {docked && (
        <div className="rp-side">
          <div className="scroll">{column(Math.min(21, Math.max(14, 360 / 30)), true)}</div>
          {weapons.length > 0 && (
            <div className="loot">
              <div className="h">ENCOUNTER LOOT</div>
              <div className="row">
                {weapons.map((l) => (
                  <button type="button" key={l.name} className="cell" onClick={() => openLoot(l.name)} title={`${l.name}  (open in D2 Info)`}>
                    <svg viewBox="0 0 100 100" width={96} height={96}><ArtCrop plan={plan} rect={l.rect} x={0} y={0} w={100} h={100} /></svg>
                    <span>{l.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <aside className="rp-rail" style={narrow ? undefined : { width: rail }}>
        <div className="top">
          <button type="button" className="rp-btn" onClick={back}>‹   All raids and dungeons</button>
          {!only && (
            <>
              <RailLabel>Step</RailLabel>
              <p className="rp-caption">
                {plan.steps.length > 1 ? <><span>Step {step + 1} of {plan.steps.length}: </span><b>{s.title}</b></> : <span className="t">{s.title}</span>}
              </p>
              <div className="rp-transport">
                <button type="button" className="rp-btn" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</button>
                <button type="button" className="rp-btn primary" disabled={step >= plan.steps.length - 1} onClick={() => setStep(step + 1)}>Next</button>
              </div>
              {plan.steps.length > 1 && (
                <div className="rp-dots">
                  {plan.steps.map((st, i) => <button type="button" key={i} className={i === step ? 'on' : ''} title={st.title} onClick={() => setStep(i)}>{i + 1}</button>)}
                </div>
              )}
            </>
          )}
          <RailLabel top>Encounters</RailLabel>
          <div className="rp-tabs">
            {plans.map((p) => (
              <button type="button" key={p.id} className={p.id === plan.id ? 'on' : ''} onClick={() => setPlanId(p.id)}>
                <b>{p.encounter}</b>
                <span>{p.kind === 'boss' ? 'Boss' : 'Encounter'}{p.steps.length > 1 ? ` · ${p.steps.length} steps` : ' · map only'}</span>
              </button>
            ))}
          </div>
          {!only && plan.roles.length > 0 && (
            <>
              <RailLabel top>Show my job</RailLabel>
              <div className="rp-roles">
                <button type="button" className={focus === null ? 'on' : ''} onClick={() => pickFocus(null)} title="Show everyone's job">Everyone</button>
                {plan.roles.map((r) => (
                  <button type="button" key={r.id} className={focus === r.id ? 'on' : ''} style={{ '--c': r.color } as CSSProperties} onClick={() => pickFocus(r.id)} title={`Show ${r.name}'s job`}>
                    <i style={{ background: r.color }} />
                    <span>{nameOf(r) ? <>{nameOf(r)}<br /></> : null}{r.name}</span>
                  </button>
                ))}
                <button type="button" className="names" onClick={() => setNamesOpen(!namesOpen)} title="Type the player names for each role">Player names...</button>
                {namesOpen && (
                  <div className="rp-names">
                    <b>Who plays what</b>
                    <p>Names for {plan.encounter}. Leave a box empty to show only the role.</p>
                    {plan.roles.map((r) => (
                      <label key={r.id}>
                        <i style={{ background: r.color }} />
                        <span>{r.name}</span>
                        <input maxLength={24} placeholder="Player name" value={names[r.id] ?? ''}
                          onChange={(e) => { const v = e.target.value; setNames((n) => ({ ...n, [r.id]: v })); store.set(`name.${plan.id}.${r.id}`, v.trim()); }} />
                      </label>
                    ))}
                    <button type="button" className="rp-btn" onClick={() => setNamesOpen(false)}>Done</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="foot">
          {plan.checkpoint && <button type="button" className="rp-cp" onClick={() => void copyCheckpoint()}>{cpText}</button>}
          {bg && (
            <label className="rp-detail">
              <RailLabel>Map art</RailLabel>
              <input type="range" min={0} max={100} step={5} value={detail} onChange={(e) => { setDetail(+e.target.value); store.set('detail', e.target.value); }} />
            </label>
          )}
          {plan.credits && <p className="rp-credits">{plan.credits.replace(' Original, unaltered.', '')}</p>}
        </div>
      </aside>
    </div>
  );
}

const RailLabel = ({ children, top }: { children: ReactNode; top?: boolean }) => <div className={`rp-raillabel ${top ? 'top' : ''}`}>{children}</div>;

// ---------------- the steps column, in pryanie's style ----------------

function StepsColumn({ plan, step, focus, names, em, docked, crota }: { plan: Plan; step: number; focus: string | null; names: Record<string, string>; em: number; docked: boolean; crota: boolean }) {
  const s = plan.steps[step] ?? plan.steps[0];
  const order = [...plan.roles].sort((a, b) => Number(b.id === focus) - Number(a.id === focus)).filter((r) => s.tasks?.[r.id]);
  return (
    <div className={`rp-col ${docked ? 'docked' : ''}`} style={{ fontSize: em }}>
      {plan.steps.length > 1 && <div className="meta">STEP {step + 1} OF {plan.steps.length}</div>}
      <h3>{s.title}</h3>
      {s.text && <p>{s.text}</p>}
      {order.length > 0 && (
        <>
          <h3>Who does what</h3>
          <ol>
            {order.map((r) => {
              const extra = [(s.enlightened ?? []).includes(r.id) ? (crota ? 'Enlightened' : 'has the buff') : '', s.holding?.[r.id] ? 'holding the ' + s.holding[r.id] : ''].filter(Boolean).join(', ');
              const who = names[r.id]?.trim();
              return (
                <li key={r.id} className={focus === r.id ? 'me' : focus ? 'dim' : ''} style={{ '--c': r.color } as CSSProperties}>
                  <i style={{ background: r.color }}>{r.id}</i>
                  <span><b>{who ? `${who}, ${r.name}` : r.name}{extra ? ` (${extra})` : ''}:</b> {s.tasks![r.id]}</span>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}

// ---------------- arrows and objects ----------------

function Arrow({ from, a, color = '#fff', ms, dim }: { from?: XY; a: { to: XY; kind: string }; color?: string; ms: number; dim: boolean }) {
  if (!from) return null;
  const thick = (a.kind === 'hit' ? 5 : 2.5) * ms;
  const dash = a.kind === 'shoot' ? [8, 6] : a.kind === 'pass' ? [] : a.kind === 'move' ? [2, 6] : [2, 4];
  const ang = Math.atan2(a.to[1] - from[1], a.to[0] - from[0]);
  const len = 6 * thick * 0.8, half = 3 * thick * 0.8;
  const tip: XY = [a.to[0] + Math.cos(ang) * thick * 0.6, a.to[1] + Math.sin(ang) * thick * 0.6];
  const p1: XY = [tip[0] - Math.cos(ang) * len - Math.sin(ang) * half, tip[1] - Math.sin(ang) * len + Math.cos(ang) * half];
  const p2: XY = [tip[0] - Math.cos(ang) * len + Math.sin(ang) * half, tip[1] - Math.sin(ang) * len - Math.cos(ang) * half];
  return (
    <g opacity={dim ? 0.15 : 0.9}>
      <line x1={from[0]} y1={from[1]} x2={a.to[0]} y2={a.to[1]} stroke={color} strokeWidth={thick} strokeDasharray={dash.map((n) => n * ms).join(' ') || undefined} />
      <polygon points={[tip, p1, p2].map((p) => p.join(',')).join(' ')} fill={color} />
    </g>
  );
}

function Label({ y, text, size, fill }: { y: number; text: string; size: number; fill: string }) {
  return <text y={y} textAnchor="middle" fill={fill} fontSize={size} fontWeight={600} fontFamily="Segoe UI, system-ui, sans-serif" paintOrder="stroke" stroke="rgba(0,0,0,.65)" strokeWidth={3}>{text}</text>;
}

const hex = (r: number) => Array.from({ length: 6 }, (_, i) => { const a = (Math.PI / 3) * i + Math.PI / 6; return `${(r * Math.cos(a)).toFixed(1)},${(r * Math.sin(a)).toFixed(1)}`; }).join(' ');
const diamond = (r: number) => `0,${-r} ${r},0 0,${r} ${-r},0`;

function MapObject({ o, plan, ms, bossBelow }: { o: Obj; plan: Plan; ms: number; bossBelow: boolean }) {
  const boss = o.type === 'crota' || o.type === 'deathsinger';
  const bossImg = plan.bossIcon?.image;
  let body: ReactNode = null;
  switch (o.type) {
    case 'crota': case 'deathsinger': {
      const bw = plan.bossIcon?.width ?? 150;
      body = (
        <>
          {bossImg ? <image href={BASE + bossImg} x={-bw / 2} y={-bw * 0.77 * 0.55} width={bw} height={bw * 0.77} />
            : plan.bossIcon?.rect && plan.map.background ? (
              <>
                <polygon points={diamond(52)} fill="rgba(226,195,65,.27)" />
                <polygon points={diamond(44)} fill="rgba(4,12,5,.85)" />
                <ArtCrop plan={plan} rect={plan.bossIcon.rect} x={-42} y={-42} w={84} h={84} clip="url(#rp-diamond)" />
              </>
            ) : (
              <>
                <polygon points={hex(34)} fill="rgba(255,79,168,.25)" stroke="#FF4FA8" strokeWidth={3} />
                <Label y={6} text={o.type === 'crota' ? 'C' : 'D'} size={20} fill="#FFE3F2" />
              </>
            )}
          {o.shield && <circle r={bossImg ? 84 : 54} fill="none" stroke="#8FD8FF" strokeWidth={3} strokeDasharray="10 6" />}
        </>
      );
      break;
    }
    case 'oversoul': body = <circle r={36} fill="url(#rp-oversoul)" />; break;
    case 'knight': body = <polygon points="0,-14 12,0 0,14 -12,0" fill="#FF9F43" stroke="#2A1400" strokeWidth={2} />; break;
    case 'swordbearer': body = <><polygon points="0,-18 15,0 0,18 -15,0" fill="#FF5E5E" stroke="#2A0000" strokeWidth={2} /><line x1={-8} y1={8} x2={18} y2={-18} stroke="#fff" strokeWidth={3} strokeLinecap="round" /></>; break;
    case 'ogre': body = <><circle r={20} fill="#9C7A3C" stroke="#2A1E08" strokeWidth={3} /><Label y={5} text="O" size={15} fill="#FFF3D6" /></>; break;
    case 'wizard': body = <polygon points="0,-15 13,10 -13,10" fill="#C77DFF" stroke="#1E0830" strokeWidth={2} />; break;
    case 'shrieker': body = <><circle r={22} fill="rgba(255,107,213,.25)" className="rp-blur" /><circle r={16} fill="#2A0F2E" stroke="#FF6BD5" strokeWidth={3} /><circle r={5} fill="#FF6BD5" /></>; break;
    case 'barrier': body = <><rect x={-26} y={-9} width={52} height={18} rx={9} fill="rgba(120,255,140,.2)" /><rect x={-22} y={-5} width={44} height={10} rx={5} fill="rgba(120,255,140,.55)" /></>; break;
    case 'lamp': body = (
      <>
        {o.lit && <circle r={26} fill="rgba(166,255,77,.35)" className="rp-blur" />}
        <circle r={o.lit ? 20 : 14} fill={o.lit ? 'rgba(166,255,77,.55)' : 'rgba(10,20,10,.6)'} stroke={o.lit ? '#D9FFB0' : '#7DDB7D'} strokeWidth={2.5} />
        <circle r={4} fill={o.lit ? '#fff' : '#7DDB7D'} />
      </>
    ); break;
    case 'totem': body = <><polygon points="0,-30 27,19 -27,19" fill="rgba(142,242,155,.12)" /><polygon points="0,-22 20,14 -20,14" fill="rgba(120,255,140,.18)" stroke="#5FD68A" strokeWidth={2.5} /></>; break;
    case 'pedestal': body = <circle r={14} fill="rgba(142,242,155,.3)" stroke="#8EF29B" strokeWidth={2} />; break;
    case 'pool': body = <circle r={46} fill="url(#rp-pool)" stroke="#FFD166" strokeWidth={2} strokeDasharray="5 5" />; break;
    case 'mark': body = (
      <>
        {o.lit && <circle r={23} fill="var(--rp-accent)" opacity={0.4} className="rp-blur" />}
        <circle r={o.lit ? 17 : 13} fill="var(--rp-accent)" fillOpacity={o.lit ? 0.45 : 0.14} stroke={o.lit ? 'var(--rp-accent2)' : 'var(--rp-accent)'} strokeOpacity={o.lit ? 1 : 0.8} strokeWidth={2.5} />
        {o.lit && <circle r={4} fill="#fff" />}
      </>
    ); break;
    case 'plate': body = (
      <>
        {o.lit && <circle r={26} fill="var(--rp-accent)" opacity={0.35} className="rp-blur" />}
        <rect x={-17} y={-17} width={34} height={34} rx={5} fill="var(--rp-accent)" fillOpacity={o.lit ? 0.4 : 0.1} stroke={o.lit ? 'var(--rp-accent2)' : 'var(--rp-accent)'} strokeOpacity={o.lit ? 1 : 0.8} strokeWidth={2.5} />
      </>
    ); break;
    case 'boss': body = (
      <>
        <polygon points={diamond(30)} fill="rgba(255,94,94,.27)" />
        <polygon points={diamond(24)} fill="rgba(30,6,8,.85)" stroke="#FF5E5E" strokeWidth={2.5} />
        {o.shield && <circle r={40} fill="none" stroke="#8FD8FF" strokeWidth={3} strokeDasharray="10 6" />}
      </>
    ); break;
    case 'enemy': body = <polygon points="0,-12 11,0 0,12 -11,0" fill="#FF7A7A" stroke="#2A0000" strokeWidth={2} />; break;
    case 'chalice': body = <><circle r={14} fill="rgba(255,209,102,.35)" className="rp-blur" /><circle r={9} fill="#FFD166" stroke="#3A2A00" strokeWidth={2} /></>; break;
    case 'sword': body = <><line x1={-14} y1={14} x2={14} y2={-14} stroke="rgba(232,236,242,.3)" strokeWidth={10} strokeLinecap="round" /><line x1={-14} y1={14} x2={14} y2={-14} stroke="#E8ECF2" strokeWidth={4} strokeLinecap="round" /></>; break;
    case 'timer': {
      const txt = (o.prefix ?? 'Oversoul') + ': ' + o.label;
      const w = 16 + txt.length * 8;
      return (
        <g transform={`translate(${o.x} ${o.y}) scale(${ms})`}>
          <rect x={-w / 2} y={-18} width={w} height={34} rx={17} fill="rgba(10,25,10,.75)" stroke="#A6FF4D" />
          <Label y={5} text={txt} size={13} fill="#D9FFB0" />
        </g>
      );
    }
  }
  const ly = boss ? (bossBelow ? (bossImg ? 70 : 62) : -(bossImg ? 92 : 58)) : o.type === 'pool' ? 62 : 32;
  return (
    <g transform={`translate(${o.x} ${o.y}) scale(${ms})`}>
      {o.type === 'oversoul' && (
        <defs>
          <radialGradient id="rp-oversoul"><stop offset="0" stopColor="#E9FFC2" /><stop offset=".38" stopColor="#A6FF4D" /><stop offset=".83" stopColor="#A6FF4D" stopOpacity="0" /></radialGradient>
        </defs>
      )}
      {o.type === 'pool' && (
        <defs><radialGradient id="rp-pool"><stop offset="0" stopColor="#FFF6D0" stopOpacity=".55" /><stop offset="1" stopColor="#FFD166" stopOpacity="0" /></radialGradient></defs>
      )}
      {body}
      {o.label && <Label y={ly} text={o.label} size={12} fill="rgba(225,240,225,.9)" />}
    </g>
  );
}

// ---------------- the map's own life (a plan file's "ambient" list, plan units) ----------------

function el(tag: string, attrs: Record<string, string | number>, parent: Element) {
  const e = document.createElementNS(NS, tag);
  for (const [k2, v] of Object.entries(attrs)) e.setAttribute(k2, String(v));
  parent.appendChild(e);
  return e;
}
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
let gradSeq = 0;
function radial(g: Element, stops: [number, string, number][]) {
  const id = `rp-amb${++gradSeq}`;
  const r = el('radialGradient', { id }, el('defs', {}, g));
  for (const [off, col, op] of stops) el('stop', { offset: off, 'stop-color': col, 'stop-opacity': op }, r);
  return `url(#${id})`;
}
function mixWhite(c: string, amount: number) {
  // c as #RRGGBB; amount of white
  const m = /^#?([0-9a-f]{6})$/i.exec(c.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16), mix = (v: number) => Math.round(255 * amount + v * (1 - amount));
  return `rgb(${mix(n >> 16)},${mix((n >> 8) & 255)},${mix(n & 255)})`;
}

function buildAmbient(layer: SVGGElement, list: any[]): ((t: number) => void)[] {
  const loops: ((t: number) => void)[] = [];
  const pt = (a: any): XY => (Array.isArray(a) && a.length >= 2 ? [a[0], a[1]] : [0, 0]);
  for (const a of list) {
    if (a.type === 'ship') {
      const at = pt(a.at);
      let size = pt(a.size); if (size[0] <= 0) size = [73, 168];
      const angle = a.angle ?? 0, back = a.back ?? 320, ahead = a.ahead ?? 260, period = Math.max(2, a.period ?? 12), delay = a.delay ?? 0;
      const color = a.color || '#CDDCE6';
      const rad = (angle * Math.PI) / 180, hx = Math.sin(rad), hy = -Math.cos(rad);
      const g = el('g', { opacity: 0 }, layer);
      const lg = el('linearGradient', { id: `rp-wake${++gradSeq}`, x1: 0, y1: 0, x2: 0, y2: 1 }, el('defs', {}, g));
      el('stop', { offset: 0, 'stop-color': color, 'stop-opacity': 0.18 }, lg); el('stop', { offset: 1, 'stop-color': color, 'stop-opacity': 0 }, lg);
      el('rect', { x: -size[0] * 0.35, y: size[1] / 2, width: size[0] * 0.7, height: size[1] * 0.9, fill: `url(#${lg.id})` }, g);
      el('rect', { x: -size[0] / 2, y: -size[1] / 2, width: size[0], height: size[1], fill: color, 'fill-opacity': 0.2, stroke: color, 'stroke-opacity': 0.85, 'stroke-width': 2.5 }, g);
      for (const f of [-0.22, 0.22]) el('line', { x1: size[0] * f, y1: -size[1] / 2 + 4, x2: size[0] * f, y2: size[1] / 2 - 4, stroke: color, 'stroke-opacity': 0.3, 'stroke-width': 1 }, g);
      el('line', { x1: -size[0] / 2 + 4, y1: 0, x2: size[0] / 2 - 4, y2: 0, stroke: color, 'stroke-opacity': 0.3, 'stroke-width': 1 }, g);
      const flying = 0.72;
      loops.push((t) => {
        const kk = ((t + delay) % period) / period;
        if (kk > flying) { g.setAttribute('opacity', '0'); return; }
        const u = kk / flying, d = -back + (back + ahead) * u;
        g.setAttribute('transform', `translate(${at[0] + hx * d} ${at[1] + hy * d}) rotate(${angle})`);
        const near = Math.exp(-(d * d) / (2 * 70 * 70));
        g.setAttribute('opacity', String(clamp01(Math.min(u / 0.2, (1 - u) / 0.2)) * (0.55 + 0.45 * near)));
      });
    } else if (a.type === 'glow') {
      const at = pt(a.at), r = a.r ?? 30, period = Math.max(0.5, a.period ?? 4), delay = a.delay ?? 0, low = a.low ?? 0.15, high = a.high ?? 0.8;
      const color = a.color || '#ffffff';
      const c = el('circle', { cx: at[0], cy: at[1], r, opacity: low }, layer);
      c.setAttribute('fill', radial(layer, [[0, color, 1], [1, color, 0]]));
      loops.push((t) => {
        const kk = 0.5 - 0.5 * Math.cos(((t + delay) / period) * 2 * Math.PI);
        c.setAttribute('opacity', String(low + (high - low) * kk));
        c.setAttribute('r', String(r * (0.85 + 0.3 * kk)));
      });
    } else if (a.type === 'beams') {
      const color = a.color || '#AAFF5A';
      const hot = mixWhite(color, 0.4);
      const nodes: Record<string, XY> = {};
      for (const [n, v] of Object.entries(a.nodes ?? {})) nodes[n] = pt(v);
      const sets: string[][] = (a.sets ?? []).map((s: string[]) => s.filter((n) => nodes[n])).filter((s: string[]) => s.length >= 2);
      if (!sets.length) continue;
      const draw = a.draw ?? 0.55, hold = a.hold ?? 5.5, fade = a.fade ?? 1.3, gap = a.gap ?? 0.9, width = a.width ?? 5, nr = a.nodeRadius ?? 46;
      const glowFill = radial(layer, [[0, hot, 0.9], [0.28, color, 0.47], [1, color, 0]]);
      const sparkFill = radial(layer, [[0, '#ffffff', 1], [1, hot, 0]]);
      const lights: Record<string, { glow: Element; core: Element; p: XY }> = {};
      for (const [n, p] of Object.entries(nodes)) {
        const glow = el('circle', { cx: p[0], cy: p[1], r: nr, fill: glowFill, opacity: 0 }, layer);
        const core = el('circle', { cx: p[0], cy: p[1], r: 6, fill: '#fff', opacity: 0 }, layer);
        lights[n] = { glow, core, p };
      }
      const sides = sets.map((set) => {
        const count = set.length === 2 ? 1 : set.length;
        return Array.from({ length: count }, (_, i) => {
          const p = nodes[set[i]], q = nodes[set[(i + 1) % set.length]];
          const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
          const group = el('g', { opacity: 0, transform: `translate(${p[0]} ${p[1]}) rotate(${(Math.atan2(q[1] - p[1], q[0] - p[0]) * 180) / Math.PI})` }, layer);
          const inner = el('g', {}, group);
          for (const [w, col, op] of [[width * 4.4, color, 0.16], [width * 1.8, color, 0.55], [width * 0.55, hot, 1]] as [number, string, number][])
            el('rect', { x: 0, y: -w / 2, width: len, height: w, rx: w / 2, fill: col, 'fill-opacity': op }, inner);
          const spark = el('circle', { r: width * 2.5, fill: sparkFill }, group);
          return { group, inner, spark, len };
        });
      });
      let shown = -1;
      loops.push((t) => {
        const n = sides[0].length;
        const cycle = draw * n + hold + fade + gap;
        const index = Math.floor(t / cycle) % sets.length;
        const u = t % cycle;
        if (index !== shown) {
          if (shown >= 0) { for (const sd of sides[shown]) sd.group.setAttribute('opacity', '0'); for (const nm of sets[shown]) { lights[nm].glow.setAttribute('opacity', '0'); lights[nm].core.setAttribute('opacity', '0'); } }
          shown = index;
        }
        const alive = u < draw * n + hold ? 1 : clamp01(1 - (u - draw * n - hold) / fade);
        sides[index].forEach((sd, i) => {
          let g = clamp01((u - draw * i) / draw);
          g = 1 - Math.pow(1 - g, 3);
          sd.inner.setAttribute('transform', `scale(${g} 1)`);
          sd.group.setAttribute('opacity', String(g <= 0 ? 0 : alive * (0.86 + 0.14 * Math.sin(t * 9 + i * 2.1))));
          const run = (t * 0.55 + i * 0.33) % 1;
          sd.spark.setAttribute('cx', String(sd.len * (g < 1 ? g : run)));
          sd.spark.setAttribute('opacity', String(g < 1 ? 1 : 0.75 * Math.sin(run * Math.PI)));
        });
        sets[index].forEach((nm, i) => {
          const lit = clamp01((u - draw * i + (i === 0 ? 0 : 0.15)) / 0.3) * alive;
          const breathe = 0.5 + 0.5 * Math.sin(t * 2.6 + i * 1.7);
          lights[nm].glow.setAttribute('opacity', String(lit * (0.7 + 0.3 * breathe)));
          lights[nm].glow.setAttribute('r', String(nr * (0.9 + 0.2 * breathe)));
          lights[nm].core.setAttribute('opacity', String(lit));
        });
      });
    }
  }
  return loops;
}

// ---------------- Copy final checkpoint ----------------

/**
 * The current checkpoint bot's join command, exactly as d2checkpoint.com's own copy button makes it ("/join Name#1234"):
 * its bot list (relayed by the site's Worker), the bot parked furthest in this raid, Standard before Master; else the
 * last code read, else the plan's fallback.
 */
async function currentCheckpoint(planId: string, cp: NonNullable<Plan['checkpoint']>): Promise<string> {
  try {
    const r = await fetch('/api/checkpoint-bots');
    if (r.ok) {
      const all = await r.json();
      const at = (i: any) => (typeof i === 'number' && i >= 0 && i < all.length ? all[i] : undefined);
      const acts = await table<any>('DestinyActivityDefinition');
      const bots = (Array.isArray(all?.[0]) ? all[0] : []).map((ref: number) => at(ref)).filter((b: any) => b && typeof b === 'object')
        .map((b: any) => ({ name: String(at(b.name) ?? ''), act: acts[String(at(b.activityHash))]?.displayProperties?.name ?? '', enc: Number(at(b.encounter) ?? -1) }))
        .filter((b: any) => b.name && b.act.toLowerCase().startsWith(cp.activity.toLowerCase()))
        .sort((a: any, b: any) => b.enc - a.enc || Number(a.act.includes('Master')) - Number(b.act.includes('Master')));
      if (bots.length) { const code = `/join ${bots[0].name}`; store.set('cp.' + planId, code); return code; }
    }
  } catch { /* d2checkpoint.com not reachable */ }
  return store.get('cp.' + planId) || cp.fallback;
}
